import React from 'react';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UIProperty from '@/components/shared/UIProperty';
import UIMap from '@/components/shared/UIMap';
import { type IProperty } from '@/types';
import './PropertyOverview.scss';

export interface PropertyOverviewProps {
  /**
   * The property object.
   */
  property?: IProperty | null;

  /**
   * Loading state.
   */
  loading?: boolean;

  /**
   * Controlled collapsed state.
   */
  collapsed?: boolean;

  /**
   * Whether to show the map.
   */
  showMap?: boolean;

  /**
   * Additional CSS class.
   */
  className?: string;

  /**
   * Collapse toggle handler.
   */
  onToggleCollapse?: (collapsed: boolean) => void;
}

/**
 * Represents a field in the property information section.
 */
interface PropertyInfoField {
  label: string;
  value: string | number | null;
  multiline?: boolean;
}

const baseClass = 'PropertyOverview';

/**
 * Displays an overview of a property, including its details and a map.
 * It is wrapped in a collapsible section.
 */
const PropertyOverview: React.FC<PropertyOverviewProps> = ({
  property,
  loading = false,
  collapsed,
  showMap = true,
  className = '',
  onToggleCollapse,
}) => {
  /**
   * Formats and returns property information fields for display.
   */
  const getPropertyInfo = (): PropertyInfoField[] => {
    if (!property) return [];

    const address = property.address;
    const latitudeText =
      address?.latitude
        ? `${address.latitude.toFixed(6)} °N`
        : 'N/A';
    const longitudeText =
       address?.longitude
        ? `${address.longitude.toFixed(6)} °W`
        : 'N/A';

    return [
      {
        label: 'Address',
        value: address
          ? `${address.street || ''}, ${address.city || ''}`.replace(
              /^,\s*|,\s*$/g,
              '',
            ) || 'N/A'
          : 'N/A',
        multiline: true,
      },
      {
        label: 'Latitude',
        value: latitudeText,
      },
      {
        label: 'Longitude',
        value: longitudeText,
      },
      {
        label: 'Units',
        value: property.unitsCount ?? property.units?.length ?? 0,
      },
    ];
  };

  /**
   * Prepares the data object required for the UIMap component.
   */
  const getMapData = () => {
    if (!property?.address || !property.address.latitude || !property.address.longitude) {
      return null;
    }

    const { latitude, longitude } = property.address;

    return {
      label: `${property.name} - ${property.address.street}, ${property.address.city}`,
      x: longitude,
      y: latitude,
      raw: {
        addresstype: 'building' as const,
        type: 'building' as const,
      },
    };
  };

  const propertyInfo = getPropertyInfo();
  const mapData = getMapData();

  // The UIMap component expects an array of data points.
  const mapDataArray = mapData ? [mapData] : [];

  const renderPropertyInfo = () => (
    <div className={`${baseClass}__info`}>
      <div className={`${baseClass}__info-content`}>
        {/* Property name and type */}
        <UIProperty
          name={property?.name || 'Unknown Property'}
          type={property?.type}
          className={`${baseClass}__name-section`}
        />

        {/* Property details table */}
        <div className={`${baseClass}__details-table`}>
          {propertyInfo.map((field: PropertyInfoField, index: number) => (
            <React.Fragment key={index}>
              <div className={`${baseClass}__field-name ${field.multiline ? `${baseClass}__field-name--multiline` : ''}`}>
                {field.label}
              </div>
              <div
                className={`${baseClass}__field-value ${field.multiline ? `${baseClass}__field-value--multiline` : ''}`}
                data-label={field.label}
              >
                {field.value || 'N/A'}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Map component - rendered only if data is available */}
      {showMap && mapDataArray.length > 0 && (
        <div className={`${baseClass}__map`}>
          <UIMap
            geoData={mapDataArray}
            streetAddress={property?.address?.street || ''}
            className={`${baseClass}__map-component`}
          />
        </div>
      )}
    </div>
  );

  return (
    <UICollapsibleSection
      title="Property overview"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading property information..."
      className={`${baseClass} ${className}`.trim()}
    >
      {renderPropertyInfo()}
    </UICollapsibleSection>
  );
};

export default PropertyOverview;
